import {
  CameraSortOption,
  enumCameraStatus,
} from '@rematter/frontend-lib-api-client';

import { CameraRecord } from '../types/sidecar';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const camerasListQueryAtom = createQueryAtomFactory(
  'settings.camerasList',
  (params: {
    ids?: string[];
    sidecarId?: string;
    facilityId?: string;
    limit?: number;
    offset?: number;
    sorting?: CameraSortOption[];
  }) => ({
    cameras: {
      __args: {
        filter: {
          ids: params.ids,
          sidecarId: params.sidecarId,
          facilityId: params.facilityId,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        sidecarId: true,
        uniqueId: true,
        driverType: true,
        name: true,
        ipAddress: true,
        manufacturer: true,
        model: true,
        firmwareVersion: true,
        status: true,
        online: true,
        initialized: true,
        streamProfile: true,
        streamUrl: true,
        snapshotUrl: true,
        lastSeenAt: true,
        hasPtz: true,
        hasFloodlight: true,
        hasSiren: true,
        sidecar: {
          id: true,
          name: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { cameras: CameraRecord[]; totalCount: number } => {
      if (!data?.cameras?.entries) {
        return { cameras: [], totalCount: 0 };
      }

      return {
        cameras: data.cameras.entries.map((entry) => ({
          id: entry.id,
          sidecarId: entry.sidecarId,
          uniqueId: entry.uniqueId,
          driverType: entry.driverType,
          name: entry.name,
          ipAddress: entry.ipAddress ?? null,
          manufacturer: entry.manufacturer ?? null,
          model: entry.model ?? null,
          firmwareVersion: entry.firmwareVersion ?? null,
          status: entry.status ?? enumCameraStatus.Discovered,
          online: entry.online ?? false,
          initialized: entry.initialized ?? false,
          streamProfile: entry.streamProfile ?? 'sub',
          streamUrl: entry.streamUrl ?? null,
          snapshotUrl: entry.snapshotUrl ?? null,
          lastSeenAt: entry.lastSeenAt
            ? parseZonedDateTime(entry.lastSeenAt)
            : null,
          hasPtz: entry.hasPtz ?? false,
          hasFloodlight: entry.hasFloodlight ?? false,
          hasSiren: entry.hasSiren ?? false,
          sidecar: entry.sidecar
            ? {
                id: entry.sidecar.id,
                name: entry.sidecar.name,
              }
            : null,
          timestamps: {
            createdAt: parseZonedDateTime(entry.timestamps.createdAt),
            updatedAt: parseZonedDateTime(entry.timestamps.updatedAt),
          },
        })),
        totalCount: data.cameras.page?.total ?? 0,
      };
    },
  },
);
